import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Stack, Card, CardContent,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  useMediaQuery, useTheme, Fab, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { api } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const emptyForm = { diagnosis_name: '', icd10_code: '', template_text: '' };

export default function PresetManager() {
  const [presets, setPresets] = useState<any[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { signOut, isGuest } = useAuth();
  const navigate = useNavigate();

  const load = () => api.getPresets().then(setPresets);
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (preset: any) => {
    setEditId(preset.id);
    setForm({ diagnosis_name: preset.diagnosis_name, icd10_code: preset.icd10_code || '', template_text: preset.template_text });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.diagnosis_name || !form.template_text) return;
    if (editId) {
      await api.updatePreset(editId, form);
    } else {
      await api.createPreset(form);
    }
    setDialogOpen(false);
    load();
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this preset?')) return;
    await api.deletePreset(id);
    load();
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant={isMobile ? 'h6' : 'h5'}>Plan Presets</Typography>
        <Stack direction="row" spacing={1}>
          {!isMobile && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Add Preset
            </Button>
          )}
          {isGuest && (
            <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => navigate('/login')}>
              Create Account
            </Button>
          )}
          <Button variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={signOut}>
            {isGuest ? 'Exit guest' : 'Sign Out'}
          </Button>
        </Stack>
      </Stack>

      {presets.map(p => (
        <Card key={p.id} sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
              <Box sx={{ minWidth: 0, flex: 1, mr: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>{p.diagnosis_name}</Typography>
                {p.icd10_code && (
                  <Chip label={`ICD-10: ${p.icd10_code}`} size="small" sx={{ mt: 0.5, bgcolor: theme.palette.accent.purple, color: theme.palette.text.primary }} />
                )}
                <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  {p.template_text}
                </Typography>
              </Box>
              <Stack direction="row" sx={{ flexShrink: 0 }}>
                <IconButton size="small" onClick={() => openEdit(p)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => handleDelete(p.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ))}
      {presets.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No presets yet. Add one to get started.</Typography>
        </Paper>
      )}

      {isMobile && (
        <Fab
          color="primary" size="medium"
          onClick={openCreate}
          sx={{ position: 'fixed', bottom: 88, right: 16, zIndex: 1000 }}
        >
          <AddIcon />
        </Fab>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editId ? 'Edit Preset' : 'Add Preset'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Diagnosis Name" required fullWidth value={form.diagnosis_name} onChange={e => setForm({ ...form, diagnosis_name: e.target.value })} />
            <TextField label="ICD-10 Code" fullWidth value={form.icd10_code} onChange={e => setForm({ ...form, icd10_code: e.target.value })} />
            <TextField
              label="Template Instructions"
              required fullWidth multiline rows={isMobile ? 8 : 6}
              value={form.template_text} onChange={e => setForm({ ...form, template_text: e.target.value })}
              placeholder="Describe how the plan should be structured for this diagnosis..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
